import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Check, X, Cog, AlertTriangle } from 'lucide-react';
import { AttendanceEntry, ExtraMachineRequest } from '../../services/attendanceApi';
import KpiRow, { Kpi } from './KpiRow';
import {
  EmployeeOption, ShiftLabel, Avatar, fmtTime, nameOf, emName, rupee, shiftTimeException,
} from './shared';

interface Props {
  pending: AttendanceEntry[];
  pendingExtra: ExtraMachineRequest[];
  employees: EmployeeOption[];
  loadingPending: boolean;
  loadingPendingExtra: boolean;
  busy: boolean;
  onApprove: (id: number) => void;
  onReject: (entry: AttendanceEntry) => void;
  onApproveMany: (ids: number[]) => void;
  onRejectMany: (ids: number[]) => void;
  onApproveExtra: (id: number) => void;
  onRejectExtra: (req: ExtraMachineRequest) => void;
}

interface DayGroup {
  key: string;
  date: string;
  entries: AttendanceEntry[];
  ageing: boolean;
  tag: string;
}

// Group pending clock-ins by calendar day (newest first) and tag each day relative to today.
const groupByDay = (entries: AttendanceEntry[]): DayGroup[] => {
  const today = dayjs().startOf('day');
  const map = new Map<string, AttendanceEntry[]>();
  entries.forEach((e) => {
    const key = dayjs(e.clockInAt).format('YYYY-MM-DD');
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(e);
  });
  return Array.from(map.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : -1)) // newest day first
    .map(([key, rows]) => {
      const diff = today.diff(dayjs(key).startOf('day'), 'day');
      const tag = diff <= 0 ? 'TODAY' : `T-${diff}`;
      return { key, date: dayjs(key).format('DD MMM YYYY'), entries: rows, ageing: diff >= 2, tag };
    });
};

const ApprovalsTab: React.FC<Props> = ({
  pending, pendingExtra, employees, loadingPending, loadingPendingExtra, busy,
  onApprove, onReject, onApproveMany, onRejectMany, onApproveExtra, onRejectExtra,
}) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const days = useMemo(() => groupByDay(pending), [pending]);
  // employeeId → ₹/machine (or ₹/hour) rate, for the extra-machine pay-impact column.
  const rateOf = useMemo(() => {
    const m = new Map<number, number>();
    employees.forEach((e) => m.set(e.id, e.extraRate || 0));
    return m;
  }, [employees]);

  const kpis: Kpi[] = useMemo(() => {
    const open = pending.length + pendingExtra.length;
    const now = dayjs();
    const ages = [...pending, ...pendingExtra].map((x) => now.diff(dayjs(x.createdAt), 'day'));
    const oldest = ages.length ? Math.max(...ages) : 0;
    const exposure = pendingExtra.reduce((a, r) => a + r.quantity * (rateOf.get(r.employeeId) || 0), 0);
    const exceptions = pending.filter((e) => shiftTimeException(e)).length;
    return [
      { label: 'Open requests', value: open, note: `${pending.length} clock-ins · ${pendingExtra.length} extra runs` },
      { label: 'Oldest item', value: `${oldest}d`, note: oldest >= 2 ? 'ageing — clear soon' : 'within a day', tone: oldest >= 2 ? 'danger' : 'default' },
      { label: 'Pay exposure', value: rupee(exposure), note: 'unapproved extra machines', tone: exposure > 0 ? 'warn' : 'default' },
      { label: 'Exceptions', value: exceptions, note: 'shift / clock-in mismatch', tone: exceptions > 0 ? 'warn' : 'default' },
    ];
  }, [pending, pendingExtra, rateOf]);

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleDay = (group: DayGroup) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = group.entries.every((e) => next.has(e.id));
      group.entries.forEach((e) => { if (allOn) next.delete(e.id); else next.add(e.id); });
      return next;
    });

  const clearSel = () => setSelected(new Set());
  const selIds = Array.from(selected);

  return (
    <div className="space-y-6 pb-20">
      <KpiRow items={kpis} />

      {/* ── Clock-in requests, grouped by day ── */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-3">
          <h2 className="text-sm font-semibold text-gray-800">Clock-in requests</h2>
          <span className="text-xs text-gray-500">Approving marks the day present · counts toward monthly pay</span>
          <div className="flex-1" />
          {pending.length > 0 && (
            <button
              onClick={() => onApproveMany(pending.map((e) => e.id))}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              <Check className="h-4 w-4" /> Approve all {pending.length}
            </button>
          )}
        </div>

        {loadingPending ? (
          <div className="px-4 py-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : pending.length === 0 ? (
          <div className="px-4 py-10 text-center text-gray-400 text-sm">No pending clock-ins. 🎉</div>
        ) : (
          days.map((group) => {
            const allOn = group.entries.every((e) => selected.has(e.id));
            return (
              <div key={group.key}>
                {/* Day header */}
                <div className="px-4 py-2.5 bg-gray-100/70 border-b border-gray-200 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={allOn}
                    onChange={() => toggleDay(group)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-semibold text-gray-800">{group.date}</span>
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${
                      group.ageing
                        ? 'text-red-600 bg-red-50 border-red-200'
                        : group.tag === 'TODAY'
                          ? 'text-blue-600 bg-blue-50 border-blue-200'
                          : 'text-gray-500 bg-gray-50 border-gray-200'
                    }`}
                  >
                    {group.ageing ? 'Ageing' : group.tag}
                  </span>
                  <span className="text-xs text-gray-500">{group.entries.length} open</span>
                  <div className="flex-1" />
                  <button
                    onClick={() => onApproveMany(group.entries.map((e) => e.id))}
                    disabled={busy}
                    className="text-xs font-semibold text-green-700 hover:underline disabled:opacity-50"
                  >
                    Approve day ({group.entries.length})
                  </button>
                </div>

                {/* Rows */}
                {group.entries.map((e) => {
                  const exception = shiftTimeException(e);
                  return (
                    <div
                      key={e.id}
                      className="px-4 py-3 border-b border-gray-100 flex items-center gap-3 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(e.id)}
                        onChange={() => toggle(e.id)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Avatar name={nameOf(e)} />
                      <div className="min-w-0 w-44">
                        <div className="text-sm font-medium text-gray-900 truncate">{nameOf(e)}</div>
                        {e.employee?.designation && (
                          <div className="text-xs text-gray-500 truncate">{e.employee.designation}</div>
                        )}
                      </div>
                      <div className="w-20 shrink-0"><ShiftLabel shift={e.shiftType} /></div>
                      <div className="w-24 shrink-0 text-sm text-gray-700">{fmtTime(e.clockInAt)}</div>
                      <div className="flex-1 min-w-0">
                        {exception && (
                          <span className="inline-flex items-start gap-1 text-xs text-amber-700 font-medium">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <span>{exception}</span>
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => onApprove(e.id)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                        >
                          <Check className="h-4 w-4" /> Approve
                        </button>
                        <button
                          onClick={() => onReject(e)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50"
                        >
                          <X className="h-4 w-4" /> Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </section>

      {/* ── Extra-machine runs (affects payable salary) ── */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-3">
          <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
            <Cog className="h-4 w-4 text-gray-400" /> Extra-machine runs
          </h2>
          <span className="text-xs font-semibold text-amber-600">Affects payable salary</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Employee', 'Date', 'Machines', 'Pay impact', 'Note', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loadingPendingExtra ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : pendingExtra.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No pending extra-machine requests. 🎉</td></tr>
              ) : (
                pendingExtra.map((r) => {
                  const rate = rateOf.get(r.employeeId) || 0;
                  const impact = r.quantity * rate;
                  const unit = r.unit === 'HOUR' ? 'hour' : 'machine';
                  return (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{emName(r)}</div>
                        {r.employee?.designation && <div className="text-xs text-gray-500">{r.employee.designation}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{dayjs(r.workDate).format('DD MMM YYYY')}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          <Cog className="h-3.5 w-3.5" /> {r.quantity}{r.unit === 'HOUR' ? ' hr' : ''}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">
                        {rate > 0
                          ? <span>{r.quantity} × {rupee(rate)} = <span className="font-semibold">{rupee(impact)}</span></span>
                          : <span className="text-gray-400">no {unit} rate set</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate" title={r.note || ''}>{r.note || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => onApproveExtra(r.id)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                          >
                            <Check className="h-4 w-4" /> Approve
                          </button>
                          <button
                            onClick={() => onRejectExtra(r)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50"
                          >
                            <X className="h-4 w-4" /> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {pendingExtra.length > 0 && (
          <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 text-right text-xs text-gray-500">
            Pay exposure{' '}
            <span className="font-semibold text-amber-700">
              {rupee(pendingExtra.reduce((a, r) => a + r.quantity * (rateOf.get(r.employeeId) || 0), 0))}
            </span>{' '}
            across {pendingExtra.length} unapproved run{pendingExtra.length === 1 ? '' : 's'}
          </div>
        )}
      </section>

      {/* ── Sticky bulk-action bar ── */}
      {selIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-[900] bg-gray-900 text-white px-5 py-3 flex items-center gap-4 shadow-lg">
          <span className="text-sm font-semibold tracking-wide">{selIds.length} selected</span>
          <button onClick={clearSel} className="text-sm text-gray-300 hover:text-white">Clear</button>
          <div className="flex-1" />
          <button
            onClick={() => { onApproveMany(selIds); clearSel(); }}
            disabled={busy}
            className="px-4 py-2 rounded-md bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
          >
            Approve selected
          </button>
          <button
            onClick={() => { onRejectMany(selIds); clearSel(); }}
            disabled={busy}
            className="px-4 py-2 rounded-md border border-gray-500 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
          >
            Reject selected
          </button>
        </div>
      )}
    </div>
  );
};

export default ApprovalsTab;
